import { forwardRef, useState } from 'react';
import { media, debug } from 'zf-web-infra';
import { Modal, message } from 'antd';
import { Upload } from '../upload';
import { t } from '../i18n';
import { useCrop } from './useCrop';
import { useTrigger } from './useTrigger';
import { usePasteUpload } from './usePasteUpload';

import styles from './style.module.less';
import type { PickerProps, PickerRef } from './types';
import type { ReactElement, Ref } from 'react';
import 'cropperjs/dist/cropper.css';

/**
 * 图片选择器，支持裁剪，单张图片并且设置了crop参数时，显示裁剪用对话框。
 * 选中的图片会上传OSS。
 *
 * 回调中，返回上传后的结果，File也一并返回可以用于临时的图片展示。
 *
 * 支持压缩（图片大于512k时），裁剪。
 *
 * // TODO: 多文件时的逻辑，多国语，OSS token的缓存
 * @param props
 * @returns
 */
function PickerComponent(props: PickerProps, ref: Ref<PickerRef>): ReactElement {
  debug.component('picker', props);
  const [isShowCropModal, setShowCropModal] = useState(false);
  const { image, initCrop, getCropImage } = useCrop({
    aspectRatio: props.aspectRatio ?? 16 / 9
  });

  if (props.paste === true) {
    usePasteUpload(async file => {
      await handleUpload(file);
    });
  }

  /**
   * 文件选择框中，选择图片后的回调
   * @param file
   * @returns
   */
  async function handleUpload(file: Blob | Blob[]): Promise<void> {
    // 如果是一张，并且crop为true，则显示裁剪对话框
    if (props.crop === true && props.multiple !== true) {
      setShowCropModal(true);
      return initCrop(file as Blob);
    }

    // 否则直接开始上传
    if (Array.isArray(file)) {
      // 如果是多文件，直接上传每个文件
      await Promise.all(file.map(async item => await doUpload(item as Blob)));
      return;
    }

    await doUpload(file as Blob);
  }

  /**
   * 上传文件到oss，并回传进度，最后获取图片尺寸返回
   * @param file
   */
  async function doUpload(file: Blob): Promise<void> {
    // TODO: 上传图片前校验文件大小，默认大小5M 等待确认
    // 上传视频大小校验，最大默认40M
    if (file.type.startsWith('video/') && file.size > ((props.maxSize ?? 40) * 1024 * 1024)) {
      message.error(t('上传视频大小不能超过{size}MB', { size: props.maxSize ?? 40 }));
      return;
    }
    let thumbnailFileKey: string | undefined = undefined;
    if (props.thumbnail === true && file.type.startsWith('video/')) {
      try {
        const thumbnail = await media.getVideoThumbnail(file);
        thumbnailFileKey = await props.onUpload(thumbnail);
      } catch (error) {
        console.error('Get thumbnail Failed');
      }
    }
    const id = await props.onUpload(file);

    props.onOk({ id,
      image: media.toImage(file),
      type: media.getFileType(file),
      thumbnail: thumbnailFileKey
    });
  }

  async function handleOk(): Promise<void> {
    setShowCropModal(false);

    const file = await getCropImage();
    await doUpload(file);
  }

  function handleCancel(): void {
    setShowCropModal(false);
    props.onCancel?.();
  }

  const { uploadRef } = useTrigger(ref);
  return (
    <>
      <Modal
        open={isShowCropModal}
        closable={false}
        maskClosable={false}
        onCancel={handleCancel}
        onOk={handleOk}
        okText={props.okName ?? t('确定')}
        cancelText={props.cancelName ?? t('取消')}
      >
        <div>
          <img ref={image} className={styles.cropImage} alt={props.alt} />
        </div>
      </Modal>
      <Upload
        accept={props.accept ?? 'image/*'}
        multiple={props.multiple}
        onUpload={handleUpload}
        ref={uploadRef}
      />
    </>
  );
}

export const Picker = forwardRef(PickerComponent);

export function compressRate(size: number): number {
  const defaultSize = 1024 * 1024;

  if (size <= defaultSize) {
    return 100;
  }

  return Math.floor(100 - ((size - defaultSize) / size * 100));
}
